import React, { useRef, useEffect, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';

// PDF.js worker setup
// Ensure we use a compatible version. If installed via npm, use that.
// If not using build system that handles worker, use CDN.
const PDFJS_VERSION = pdfjsLib.version || '3.11.174';
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${PDFJS_VERSION}/build/pdf.worker.min.js`;

const drawBoxPath = (ctx, box, scale = 1) => {
    if (!box || box.length !== 4) return;

    ctx.beginPath();
    ctx.moveTo(box[0][0] * scale, box[0][1] * scale);
    for (let i = 1; i < 4; i++) {
        ctx.lineTo(box[i][0] * scale, box[i][1] * scale);
    }
    ctx.closePath();
};

const drawStroke = (ctx, box, color = 'red', lineWidth = 2, scale = 1) => {
    drawBoxPath(ctx, box, scale);
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.stroke();
};

const isPointInPoly = (point, vs) => {
    // 🎯 Null safety: vs가 없거나 배열이 아니면 false
    if (!vs || !Array.isArray(vs) || vs.length === 0) return false;

    // ray-casting algorithm
    const x = point[0], y = point[1];
    let inside = false;
    for (let i = 0, j = vs.length - 1; i < vs.length; j = i++) {
        if (!vs[i] || !vs[j]) continue;  // null safety
        const xi = vs[i][0], yi = vs[i][1];
        const xj = vs[j][0], yj = vs[j][1];

        const intersect = ((yi > y) !== (yj > y))
            && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
        if (intersect) inside = !inside;
    }
    return inside;
};

// 🎯 {x,y,width,height} bbox를 폴리곤으로 변환
const bboxToPolygon = (bbox) => {
    if (!bbox || typeof bbox.x !== 'number') return null;
    return [
        [bbox.x, bbox.y],
        [bbox.x + bbox.width, bbox.y],
        [bbox.x + bbox.width, bbox.y + bbox.height],
        [bbox.x, bbox.y + bbox.height]
    ];
};

// 🎯 점이 {x,y,width,height} bbox 안에 있는지 확인
const isPointInBbox = (point, bbox) => {
    if (!bbox || typeof bbox.x !== 'number') return false;
    const [px, py] = point;
    return px >= bbox.x && px <= bbox.x + bbox.width &&
        py >= bbox.y && py <= bbox.y + bbox.height;
};

const OCRLabelingViewer = ({ imageUrl, initialBoundingBoxes = [], onSave, fileType = 'image', isHybridMode = false, ocrImageSize = null, pageNumber = 1 }) => {
    const contentCanvasRef = useRef(null);
    const overlayCanvasRef = useRef(null);
    const containerRef = useRef(null);

    const [image, setImage] = useState(null); // Image object
    const [pdfPage, setPdfPage] = useState(null); // PDF Page Proxy

    const [scale, setScale] = useState(1);
    const [ocrScale, setOcrScale] = useState({ x: 1, y: 1 });
    // ⭐ 수동 오프셋 보정 (localStorage 연동)
    const [manualOffset, setManualOffset] = useState(() => {
        const saved = localStorage.getItem('ocr_manual_offset');
        return saved ? JSON.parse(saved) : { x: 0, y: 0 };
    });
    const [boundingBoxes, setBoundingBoxes] = useState(initialBoundingBoxes);
    const [hoveredIndex, setHoveredIndex] = useState(-1);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [editValue, setEditValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (initialBoundingBoxes) {
            setBoundingBoxes(initialBoundingBoxes);
        }
        // ⭐ 디버그: 전달된 데이터 확인
        console.log('🔍 [OCRLabelingViewer] Props:', {
            imageUrl: imageUrl?.substring(0, 50) + '...',
            fileType,
            boxCount: initialBoundingBoxes?.length,
            ocrImageSize,
            isHybridMode
        });
    }, [initialBoundingBoxes, imageUrl, fileType, ocrImageSize, isHybridMode]);

    // Load Resource
    useEffect(() => {
        if (!imageUrl) return;

        const loadResource = async () => {
            setIsLoading(true);
            setImage(null);
            setPdfPage(null);

            try {
                // Determine if PDF based on prop or extension (blob URLs usually don't have extension, rely on fileType)
                // App.js passes 'fileType' implicitly? No, previous App.js code didn't pass 'fileType'.
                // But we can check if it looks like a PDF signature if it's a blob, or trust the user.
                // Since App.js 'selectedFile' object usually has .file.type, we should pass it.
                // For now, heuristic:
                // ⭐ 수정: Data URL(이미지)인 경우 PDF가 아니라고 판단 (백엔드 생성 이미지를 우선 사용)
                const isDataUrl = imageUrl && imageUrl.startsWith('data:image/');
                const isPdf = !isDataUrl && ((fileType && fileType.includes('pdf')) || imageUrl.toLowerCase().endsWith('.pdf'));

                if (isPdf) {
                    const loadingTask = pdfjsLib.getDocument(imageUrl);
                    const pdf = await loadingTask.promise;
                    const targetPage = Math.max(1, Math.min(pageNumber, pdf.numPages));
                    const page = await pdf.getPage(targetPage);
                    setPdfPage(page);
                } else {
                    await new Promise((resolve, reject) => {
                        const img = new Image();
                        img.src = imageUrl;
                        img.onload = () => {
                            setImage(img);
                            resolve();
                        };
                        img.onerror = () => {
                            // If image fails, try PDF (sometimes blob mime type is mixed up)
                            // But usually onerror is enough to stop.
                            reject(new Error("Image load failed"));
                        };
                    });
                }
            } catch (err) {
                console.error("Resource load error:", err);
                setIsLoading(false);
            } finally {
                setIsLoading(false);
            }
        };

        loadResource();
    }, [imageUrl, pageNumber, fileType]); // ⭐ pageNumber 의존성 추가

    // 🚀 Native PDF View (iframe) condition
    const showNativePdf = isHybridMode && fileType === 'application/pdf' && imageUrl;
    const nativePdfUrl = showNativePdf ? `${imageUrl}#page=${pageNumber}&view=FitH` : null;

    const renderTaskRef = useRef(null);

    // Render Content (Image/PDF)
    useEffect(() => {
        if (!containerRef.current) return;
        if (showNativePdf) return; // Skip canvas rendering if showing native PDF

        const renderContent = async () => {
            if (!contentCanvasRef.current) return;

            // Cancel previous render task if any
            if (renderTaskRef.current) {
                try {
                    await renderTaskRef.current.cancel();
                } catch (e) {
                    // Cancellation error is expected
                    // console.warn("Previous render task cancellation failed:", e);
                }
                renderTaskRef.current = null;
            }

            const ctx = contentCanvasRef.current.getContext('2d');
            const containerWidth = containerRef.current.clientWidth;
            let newScale = 1;
            let actualImageWidth = 0;
            let actualImageHeight = 0;

            if (pdfPage) {
                const viewport = pdfPage.getViewport({ scale: 1.0 });
                actualImageWidth = viewport.width;
                actualImageHeight = viewport.height;
                newScale = containerWidth / viewport.width;
                const scaledViewport = pdfPage.getViewport({ scale: newScale });

                contentCanvasRef.current.width = scaledViewport.width;
                contentCanvasRef.current.height = scaledViewport.height;

                const renderTask = pdfPage.render({
                    canvasContext: ctx,
                    viewport: scaledViewport
                });

                renderTaskRef.current = renderTask;

                try {
                    await renderTask.promise;
                } catch (err) {
                    if (err.name === 'RenderingCancelledException') {
                        // Ignore cancellation
                        return;
                    }
                    console.error("PDF Render Error:", err);
                } finally {
                    renderTaskRef.current = null;
                }

            } else if (image) {
                // ⭐ 이미지 로딩 상태 재확인
                if (!image.complete) {
                    console.log('⏳ [OCRLabelingViewer] Image not complete yet, waiting...');
                    return;
                }

                // ⭐ 원본 이미지 크기 (naturalWidth/naturalHeight)
                actualImageWidth = image.naturalWidth || image.width;
                actualImageHeight = image.naturalHeight || image.height;

                if (actualImageWidth === 0 || actualImageHeight === 0) {
                    console.warn('⚠️ [OCRLabelingViewer] Image size is 0x0, skipping render');
                    return;
                }

                newScale = containerWidth / actualImageWidth;
                contentCanvasRef.current.width = actualImageWidth * newScale;
                contentCanvasRef.current.height = actualImageHeight * newScale;

                ctx.drawImage(image, 0, 0, contentCanvasRef.current.width, contentCanvasRef.current.height);
            }

            setScale(newScale);

            // ⭐ OCR 좌표 스케일 계산 (OCR 이미지 크기 → 표시 캔버스 크기)
            // ocrImageSize는 PaddleOCR이 처리한 이미지 크기
            // displayedWidth는 화면에 표시된 캔버스 크기
            if (ocrImageSize && ocrImageSize.width > 0 && ocrImageSize.height > 0 && contentCanvasRef.current && contentCanvasRef.current.width > 0) {
                const displayedWidth = contentCanvasRef.current.width;
                const displayedHeight = contentCanvasRef.current.height;
                const ocrScaleX = displayedWidth / ocrImageSize.width;
                const ocrScaleY = displayedHeight / ocrImageSize.height;

                // ⭐ Aspect Ratio가 달라도 각각의 비율대로 스케일링 적용 (좌표 밀림 방지)
                if (isFinite(ocrScaleX) && isFinite(ocrScaleY) && ocrScaleX > 0 && ocrScaleY > 0) {
                    setOcrScale({ x: ocrScaleX, y: ocrScaleY });
                    // console.log(`🔍 [OCRScale] OCR Image: ${ocrImageSize.width}x${ocrImageSize.height} → Display: ${displayedWidth.toFixed(0)}x${displayedHeight.toFixed(0)}`);
                    // console.log(`   Scale X: ${ocrScaleX.toFixed(4)}, Scale Y: ${ocrScaleY.toFixed(4)}`);
                } else {
                    console.warn(`⚠️ [OCRScale] Invalid scale calculated`);
                    setOcrScale({ x: newScale, y: newScale });
                }
            } else if (actualImageWidth > 0) {
                // ocrImageSize가 없으면 로드된 이미지 기준으로 스케일 계산
                const displayedWidth = contentCanvasRef.current.width;
                // 세로는 displayedHeight를 써야 정확함 if image is stretched
                const displayedHeight = contentCanvasRef.current.height;
                const ratioX = displayedWidth / actualImageWidth;
                const ratioY = displayedHeight / actualImageHeight;

                setOcrScale({ x: ratioX, y: ratioY });
                console.log(`🔍 [OCRScale] No ocrImageSize, using actual image. Scale X: ${ratioX.toFixed(4)}, Y: ${ratioY.toFixed(4)}`);
            } else {
                setOcrScale({ x: newScale, y: newScale });
            }
        };

        renderContent();

        const handleResize = () => renderContent();
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            if (renderTaskRef.current) {
                renderTaskRef.current.cancel();
            }
        };

    }, [image, pdfPage, ocrImageSize, showNativePdf]);

    // Render Overlay
    useEffect(() => {
        if (showNativePdf) return; // Skip overlay if showing native PDF
        if (!overlayCanvasRef.current || !contentCanvasRef.current) return;

        const overlay = overlayCanvasRef.current;
        const content = contentCanvasRef.current; // To sync size

        // Sync size
        if (overlay.width !== content.width || overlay.height !== content.height) {
            overlay.width = content.width;
            overlay.height = content.height;
        }

        const ctx = overlay.getContext('2d');
        ctx.clearRect(0, 0, overlay.width, overlay.height);

        boundingBoxes.forEach((item, index) => {
            const isHovered = index === hoveredIndex;
            const isSelected = index === selectedIndex;

            // 🎯 녹색 점선 스타일 (라벨링 팝업과 동일)
            let strokeColor = '#22C55E';  // 기본: 녹색

            if (isHovered) {
                strokeColor = '#F59E0B';  // 호버: 주황색
            }
            if (isSelected) {
                strokeColor = '#EF4444';  // 선택: 빨간색
            }

            // Fill - ocrScale 사용 (OCR 좌표 → 디스플레이 좌표) + ⭐ 수동 오프셋 적용
            // 🎯 bbox 형식 호환: {x,y,width,height} 또는 [[x,y], [x,y], ...]
            let boxX, boxY, boxWidth, boxHeight;

            if (item.bbox && typeof item.bbox.x === 'number') {
                // {bbox: {x, y, width, height}} 형식
                boxX = (item.bbox.x * ocrScale.x) + manualOffset.x;
                boxY = (item.bbox.y * ocrScale.y) + manualOffset.y;
                boxWidth = item.bbox.width * ocrScale.x;
                boxHeight = item.bbox.height * ocrScale.y;
            } else if (item.box && Array.isArray(item.box) && Array.isArray(item.box[0])) {
                // [[x1,y1], [x2,y2], ...] 형식
                boxX = (item.box[0][0] * ocrScale.x) + manualOffset.x;
                boxY = (item.box[0][1] * ocrScale.y) + manualOffset.y;
                boxWidth = (item.box[1][0] - item.box[0][0]) * ocrScale.x;
                boxHeight = (item.box[2][1] - item.box[0][1]) * ocrScale.y;
            } else {
                // 지원하지 않는 형식 - 스킵
                return;
            }

            // 유효성 검사
            if (boxWidth <= 0 || boxHeight <= 0) return;

            // 🎯 점선 스타일 설정
            ctx.setLineDash([4, 2]);  // 점선: 4px 선, 2px 공백

            ctx.beginPath();
            ctx.rect(boxX, boxY, boxWidth, boxHeight);

            // 🎯 채우기 없음 (투명)
            // ctx.fillStyle = fillColor;
            // ctx.fill();

            // Stroke
            ctx.strokeStyle = strokeColor;
            ctx.lineWidth = isSelected ? 2 : 1.5;
            ctx.stroke();

            // 점선 리셋 (다음 그리기를 위해)
            ctx.setLineDash([]);

            // ⭐ 라벨 표시 (key + value)
            const labelText = item.key || '';
            const valueText = item.text ? String(item.text).substring(0, 30) : '';
            const labelBgColor = item.key ? 'rgba(0, 150, 0, 0.85)' : 'rgba(50, 100, 200, 0.85)';

            if (labelText) {
                ctx.font = 'bold 10px Arial, sans-serif';
                const labelWidth = ctx.measureText(labelText).width + 6;
                const labelHeight = 14;

                // 라벨 배경
                ctx.fillStyle = labelBgColor;
                ctx.fillRect(boxX, boxY - labelHeight - 2, labelWidth, labelHeight);

                // 라벨 텍스트
                ctx.fillStyle = 'white';
                ctx.fillText(labelText, boxX + 3, boxY - 5);
            }

            // ⭐ 값 표시 (호버 또는 선택 시에만)
            if ((isHovered || isSelected) && valueText) {
                ctx.font = '11px Arial, sans-serif';
                const valueWidth = ctx.measureText(valueText).width + 8;
                const valueHeight = 16;
                const valueY = boxY + 2;

                // 값 배경 (반투명 흰색)
                ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
                ctx.fillRect(boxX, valueY, valueWidth, valueHeight);
                ctx.strokeStyle = strokeColor;
                ctx.lineWidth = 1;
                ctx.strokeRect(boxX, valueY, valueWidth, valueHeight);

                // 값 텍스트
                ctx.fillStyle = '#333';
                ctx.fillText(valueText, boxX + 4, valueY + 12);
            }
        });

    }, [boundingBoxes, hoveredIndex, selectedIndex, scale, ocrScale, manualOffset, image, pdfPage, showNativePdf]);

    // ⭐ 오프셋 변경 핸들러
    const updateOffset = (dx, dy) => {
        setManualOffset(prev => {
            const newOffset = { x: prev.x + dx, y: prev.y + dy };
            localStorage.setItem('ocr_manual_offset', JSON.stringify(newOffset));
            return newOffset;
        });
    };

    const handleMouseMove = (e) => {
        if (!overlayCanvasRef.current) return;
        const rect = overlayCanvasRef.current.getBoundingClientRect();
        // 마우스 좌표에서 오프셋을 빼고 스케일로 나눠야 OCR 좌표가 됨
        const x = (e.clientX - rect.left - manualOffset.x) / ocrScale.x;
        const y = (e.clientY - rect.top - manualOffset.y) / ocrScale.y;

        // 🎯 bbox 형식 호환: {bbox: {x,y,w,h}} 또는 {box: [[x,y],...]} 모두 지원
        const foundIndex = boundingBoxes.findIndex(item => {
            if (item.bbox && typeof item.bbox.x === 'number') {
                return isPointInBbox([x, y], item.bbox);
            } else if (item.box && Array.isArray(item.box)) {
                return isPointInPoly([x, y], item.box);
            }
            return false;
        });
        setHoveredIndex(foundIndex);
    };

    const handleClick = () => {
        if (hoveredIndex !== -1) {
            setSelectedIndex(hoveredIndex);
            setEditValue(boundingBoxes[hoveredIndex].text || '');
        } else {
            setSelectedIndex(-1);
        }
    };

    const handleSaveEdit = () => {
        if (selectedIndex === -1) return;
        const newBoxes = [...boundingBoxes];
        newBoxes[selectedIndex] = { ...newBoxes[selectedIndex], text: editValue };
        setBoundingBoxes(newBoxes);
        if (onSave) onSave(newBoxes);
        setSelectedIndex(-1);
    };

    return (
        <div className="flex h-full w-full relative">
            <div ref={containerRef} className="flex-1 overflow-auto bg-gray-100 flex justify-center p-4 relative">
                {showNativePdf ? (
                    <div className="w-full h-full bg-gray-100 flex flex-col">
                        <iframe
                            src={nativePdfUrl}
                            className="w-full h-full border-none"
                            title="Native PDF Viewer"
                        />
                    </div>
                ) : (
                    <div className="relative shadow-lg">
                        {/* Layer 1: Content */}
                        <canvas ref={contentCanvasRef} className="block bg-white" />

                        {/* Layer 2: Overlay */}
                        <canvas
                            ref={overlayCanvasRef}
                            className="absolute top-0 left-0 cursor-crosshair"
                            onMouseMove={handleMouseMove}
                            onClick={handleClick}
                        />
                    </div>
                )}

                {isLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100/80 z-50">
                        <div className="text-gray-600 font-medium animate-pulse">Loading Document...</div>
                    </div>
                )}

            </div>


            {/* ⭐ 좌표 미세 조정 컨트롤러 */}
            {!showNativePdf && (
                <div className="absolute top-4 right-4 bg-white/90 p-2 rounded shadow-lg border border-gray-300 z-10 flex flex-col items-center gap-2">
                    <span className="text-xs font-bold text-gray-700">좌표 보정</span>
                    <div className="flex items-center gap-1">
                        <button onClick={() => updateOffset(0, -1)} className="p-1 hover:bg-gray-200 rounded" title="위로">⬆️</button>
                    </div>
                    <div className="flex items-center gap-1">
                        <button onClick={() => updateOffset(-1, 0)} className="p-1 hover:bg-gray-200 rounded" title="왼쪽">⬅️</button>
                        <button onClick={() => {
                            setManualOffset({ x: 0, y: 0 });
                            localStorage.removeItem('ocr_manual_offset');
                        }} className="text-xs px-1 text-gray-500 hover:text-red-500" title="초기화">R</button>
                        <button onClick={() => updateOffset(1, 0)} className="p-1 hover:bg-gray-200 rounded" title="오른쪽">➡️</button>
                    </div>
                    <div className="flex items-center gap-1">
                        <button onClick={() => updateOffset(0, 1)} className="p-1 hover:bg-gray-200 rounded" title="아래로">⬇️</button>
                    </div>
                    <div className="text-[10px] text-gray-500">
                        X: {manualOffset.x}, Y: {manualOffset.y}
                    </div>
                </div>
            )}

            {/* Editor Popup */}
            {selectedIndex !== -1 && boundingBoxes[selectedIndex] && !showNativePdf && (() => {
                const selectedBox = boundingBoxes[selectedIndex];
                // 🎯 bbox 형식 호환: {bbox: {x,y,w,h}} 또는 {box: [[x,y],...]}
                let popupTop, popupLeft;
                if (selectedBox.bbox && typeof selectedBox.bbox.x === 'number') {
                    popupTop = ((selectedBox.bbox.y + selectedBox.bbox.height) * ocrScale.y) + manualOffset.y + 20;
                    popupLeft = (selectedBox.bbox.x * ocrScale.x) + manualOffset.x;
                } else if (selectedBox.box && Array.isArray(selectedBox.box) && selectedBox.box[2]) {
                    popupTop = (selectedBox.box[2][1] * ocrScale.y) + manualOffset.y + 20;
                    popupLeft = (selectedBox.box[0][0] * ocrScale.x) + manualOffset.x;
                } else {
                    popupTop = 100;
                    popupLeft = 100;
                }
                return (
                    <div
                        className="absolute bg-white border border-gray-300 shadow-xl p-3 rounded z-10 w-64"
                        style={{
                            top: popupTop,
                            left: popupLeft
                        }}
                    >
                        <div className="text-xs text-gray-500 mb-1 font-mono">
                            {boundingBoxes[selectedIndex].key || 'Raw Text'}
                        </div>
                        <input
                            type="text"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSaveEdit()}
                            className="border p-1 w-full mb-2 text-sm rounded"
                            autoFocus
                        />
                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => setSelectedIndex(-1)}
                                className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveEdit}
                                className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 shadow-sm"
                            >
                                Save
                            </button>
                        </div>
                    </div>
                );
            })()}

            {/* Mode Badge */}
            <div className={`absolute bottom-4 left-4 px-3 py-1 rounded-full text-xs font-semibold shadow-lg z-10 ${showNativePdf ? 'bg-purple-600 text-white' : 'bg-gray-600 text-white'}`}>
                {showNativePdf ? 'PDF Native Viewer' : (isHybridMode ? 'OCR Visualization (Hybrid Mode)' : 'OCR Visualization (Standard Mode)')}
            </div>
        </div>
    );
};

export default OCRLabelingViewer;
